'use client'

import { Icon } from '@/components/ui/icon'
import { IconName } from '@/config/icons'
import { IMediaItem } from '@/lib/types/media/core'
import { cn, Skeleton } from '@heroui/react'

/** Compatibility type replacing HeroUI v2 ImageProps (removed in v3) */
type NextUIImageProps = React.ImgHTMLAttributes<HTMLImageElement>
import { MediaPlacement, MediaType } from '@prisma/client'
import { useEffect, useState } from 'react'

export interface ContentImageProps extends Omit<NextUIImageProps, 'src' | 'onError'> {
  src?: string | null
  media?: IMediaItem | null // Support MediaItem objects directly
  fallbackSrc?: string
  contentType?: 'PRODUCT' | 'PROJECT' | 'ARTICLE' | 'LOCATION' | 'SERVICE' | 'NONE'
  mediaType?: MediaType
  placement?: MediaPlacement
  showSkeleton?: boolean
  showCaption?: boolean
  onLoadComplete?: () => void
  onError?: (error: Event) => void
}

/**
 * ContentImage - A wrapper around NextUI Image component that handles:
 * - Local seed images from /seed/misc/
 * - Media service processed images
 * - Proper fallbacks and error handling
 * - Loading states with skeleton
 * - Content type specific optimizations
 */
export function ContentImage({
  src,
  media,
  alt,
  fallbackSrc = '/images/placeholder.jpg',
  contentType = 'NONE',
  mediaType = MediaType.IMAGE,
  placement,
  className,
  showSkeleton = true,
  showCaption = false,
  onLoadComplete,
  onError,
  loading = 'lazy',
  ...props
}: ContentImageProps) {
  // Determine the best source URL to use. Returns an empty string when there
  // is no real media/src to try, rather than a raster fallback file: the
  // fallback is now an inline token-driven placeholder tile (see render below).
  const getBestSrc = (): string => {
    if (media?.url) return media.url
    if (src) return src
    return ''
  }

  // Determine the best alt text to use
  const getBestAlt = (): string => {
    if (alt) return alt
    if (media?.altText) return media.altText
    if (media?.caption) return media.caption
    return `${contentType} image`
  }

  // Determine the best media type to use
  const getBestMediaType = (): MediaType => {
    if (media?.type) return media.type
    return mediaType
  }

  const [imageSrc, setImageSrc] = useState<string>(getBestSrc())
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    // Reset error/loading state whenever the resolved source changes so a
    // caller swapping media after a prior failure gets a fresh attempt.
    setImageSrc(getBestSrc())
    setHasError(false)
    setIsLoading(true)
  }, [src, media])

  const handleLoad = () => {
    setIsLoading(false)
    setHasError(false)
    onLoadComplete?.()
  }

  const handleError = (event: any) => {
    console.warn(`Failed to load image: ${imageSrc}`)
    setHasError(true)
    setIsLoading(false)
    onError?.(event)
  }

  // Get optimized sizes based on content type
  const getSizeClasses = () => {
    switch (contentType) {
      case 'PRODUCT':
        return 'w-full h-full object-cover'
      case 'SERVICE':
        return 'w-full h-full object-cover'
      case 'PROJECT':
        return 'w-full h-full object-cover'
      case 'ARTICLE':
        return 'w-full h-auto object-cover'
      case 'LOCATION':
        return 'w-full h-64 object-cover'
      default:
        return 'w-full h-auto object-cover'
    }
  }

  // Icon shown in the placeholder tile when there is no resolvable image.
  // Matches the shared ImagePlaceholder component so every empty image slot
  // reads the same across the app.
  const placeholderIcon: IconName = 'image'

  // House rule: an imageless slot must say what asset is needed, not pose as
  // content. Callers that already pass an "Image needed: ..." alt keep it;
  // otherwise the label is derived from the content type + alt.
  const placeholderNoun: Record<string, string> = {
    PRODUCT: 'product photo',
    SERVICE: 'service photo',
    PROJECT: 'project photo',
    ARTICLE: 'article image',
    LOCATION: 'location photo'
  }

  const sizeClasses = getSizeClasses()
  const actualMediaType = getBestMediaType()
  const actualAlt = getBestAlt()
  const hasResolvableSrc = imageSrc.length > 0

  // For video content, render a video element instead
  if (actualMediaType === MediaType.VIDEO) {
    return (
      <div className='relative w-full h-full'>
        <video src={imageSrc} className={cn(sizeClasses, className)} controls muted loop playsInline>
          Your browser does not support the video tag.
        </video>
        {showCaption && (media?.caption || media?.altText) && (
          <p className='text-xs text-muted mt-2 line-clamp-2'>{media?.caption || media?.altText}</p>
        )}
      </div>
    )
  }

  // No real media/src to try, or the <img> failed to load: render an inline,
  // token-driven placeholder tile instead of attempting a raster fallback
  // file (those are baked dark and don't respond to theme). The tile is
  // absolutely positioned, so its wrapper needs real dimensions: an
  // <img> gets its box from its own intrinsic size,
  // but an absolutely-positioned child never contributes to an ancestor's
  // auto height, so without w-full h-full here the whole thing (and any
  // enclosing <Link>) collapses to zero height.
  if (!hasResolvableSrc || hasError) {
    const placeholderLabel = actualAlt.startsWith('Image needed')
      ? actualAlt
      : `Image needed: ${placeholderNoun[contentType] ?? 'image'} of ${actualAlt}`
    return (
      <div className='relative w-full h-full'>
        <div
          role='img'
          aria-label={placeholderLabel}
          title={placeholderLabel}
          className={cn(
            'absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-gray-200',
            className
          )}
        >
          <Icon icon={placeholderIcon} className='text-2xl text-gray-500' aria-hidden='true' />
          <span className='text-xs text-muted line-clamp-2 px-2 text-center' aria-hidden='true'>
            {placeholderLabel}
          </span>
        </div>

        {showCaption && (media?.caption || media?.altText) && (
          <p className='text-xs text-muted mt-2 line-clamp-2'>{media?.caption || media?.altText}</p>
        )}
      </div>
    )
  }

  return (
    // The wrapper takes the parent's box rather than shrinking to the image.
    // It is the containing block for the loading skeleton, and every caller
    // sizes the <img> with h-full: against an auto-height wrapper that
    // percentage is indeterminate, so the image kept its intrinsic aspect,
    // object-cover never engaged, and a portrait photo overflowed a 16:10 card
    // and was clipped at the bottom. Same reasoning as the placeholder branch.
    <div className='relative w-full h-full'>
      {showSkeleton && isLoading && <Skeleton className={cn('absolute inset-0 z-10', sizeClasses)} />}

      <img
        src={imageSrc}
        alt={actualAlt}
        className={cn(sizeClasses, 'transition-transform', className)}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError as any}
      />

      {showCaption && (media?.caption || media?.altText) && (
        <p className='text-xs text-muted mt-2 line-clamp-2'>{media?.caption || media?.altText}</p>
      )}
    </div>
  )
}

/**
 * ContentGallery - Renders a gallery of images for content
 */
export interface ContentGalleryProps {
  images: Array<{
    url?: string | null
    altText?: string | null
    caption?: string | null
    type?: MediaType
  }>
  media?: IMediaItem[] // Support MediaItem arrays directly
  contentType?: ContentImageProps['contentType']
  className?: string
  imageClassName?: string
  fallbackSrc?: string
  columns?: 2 | 3 | 4
  gap?: 'sm' | 'md' | 'lg'
  showCaptions?: boolean
  enableLightbox?: boolean
}

export function ContentGallery({
  images,
  media,
  contentType = 'NONE',
  className,
  imageClassName,
  fallbackSrc = '/images/placeholder.jpg',
  columns = 3,
  gap = 'md',
  showCaptions = false,
  enableLightbox = false
}: ContentGalleryProps) {
  // Determine which data source to use - prefer media over images
  const mediaItems = media || images

  if (!mediaItems || mediaItems.length === 0) {
    return null
  }

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6'
  }

  const gridClasses = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4'
  }

  return (
    <div className={cn('grid', gridClasses[columns], gapClasses[gap], className)}>
      {mediaItems.map((item, index) => {
        // Check if it's a MediaItem or legacy format
        const isMediaItem = 'id' in item
        const imageProps = isMediaItem
          ? { media: item as IMediaItem }
          : {
              src: item.url,
              alt: item.altText || `Gallery image ${index + 1}`,
              mediaType: item.type || MediaType.IMAGE
            }

        return (
          <div key={isMediaItem ? (item as IMediaItem).id : index} className='relative group'>
            <ContentImage
              {...imageProps}
              contentType={contentType}
              className={cn(
                'rounded-lg overflow-hidden',
                'group-hover:scale-105 transition-transform',
                enableLightbox && 'cursor-pointer',
                imageClassName
              )}
              fallbackSrc={fallbackSrc}
              showCaption={showCaptions}
            />
          </div>
        )
      })}
    </div>
  )
}
